"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ProductTeaser } from "@/types/productTeaser";

export async function fetchProductTeasers(
  ids: string[],
): Promise<ProductTeaser[]> {
  const products: ProductTeaser[] = [];
  // The API accepts at most 50 IDs. Keep requests bounded even for large cards.
  for (let offset = 0; offset < ids.length; offset += 50) {
    const response = await fetch("/api/teaser/product-list", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ids: ids.slice(offset, offset + 50) }),
    });
    if (!response.ok) throw new Error("Failed to fetch product images");
    const data = (await response.json()) as { products?: ProductTeaser[] };
    products.push(...(data.products ?? []));
  }
  return products;
}

export function useProductTeasers(productIds: (string | undefined)[]) {
  const ids = Array.from(
    new Set(productIds.filter((id): id is string => Boolean(id))),
  ).sort();
  const query = useQuery({
    queryKey: ["current-product-teasers", ids],
    queryFn: () => fetchProductTeasers(ids),
    enabled: ids.length > 0,
    staleTime: 300_000,
  });
  const products = useMemo(
    () =>
      Object.fromEntries(
        (query.data ?? []).map((product) => [product.id, product]),
      ),
    [query.data],
  );
  return { ...query, products };
}
